import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigableProps } from '../utils/navigation';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  alpha,
  useTheme,
  TextField,
  MenuItem,
  Select,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { useSandboxFreigaben } from '../hooks/useSandboxFreigaben';
import PageHeader from '../components/common/PageHeader';
import TableSkeleton from '../components/common/TableSkeleton';
import { auditsService } from '../services/auditsService';
import { auditStatusService, auditTypesService } from '../services/lookupService';
import { useSearchParamsState } from '../hooks/useSearchParamsState';
import { AuditList, AuditStatus, AuditType } from '../types';
import { isClosedStatus } from '../utils/workflowStatus';

type SortField = 'name' | 'projektnummer' | 'kunde' | 'audit_type' | 'status' | 'schwachstellen_count' | 'erstellt_am';

interface Filters {
  name: string;
  projektnummer: string;
  kunde: string;
  audit_type: string;
  status: string;
  schwachstellen_count: string;
  erstellt_am: string;
}

const EMPTY_FILTERS: Filters = {
  name: '',
  projektnummer: '',
  kunde: '',
  audit_type: '',
  status: '',
  schwachstellen_count: '',
  erstellt_am: '',
};

/* ─── Komponente ─── */
const AuditsListPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { isAuditShared } = useSandboxFreigaben();

  /* Daten */
  const [audits, setAudits] = useState<AuditList[]>([]);
  const [auditStatuses, setAuditStatuses] = useState<AuditStatus[]>([]);
  const [auditTypes, setAuditTypes] = useState<AuditType[]>([]);
  const [loading, setLoading] = useState(true);

  /* Filter + Sort via URL-Params */
  const [params, setParams, resetParams, hasActiveFilters] = useSearchParamsState({
    defaults: {
      name: '',
      nummer: '',
      kunde: '',
      typ: '',
      status: '',
      schwachstellen: '',
      erstellt: '',
      sort: '',
      beendet: false as boolean,
    },
  });

  // Abgeleitete Werte aus URL-Params
  const filters: Filters = {
    name: params.name as string,
    projektnummer: params.nummer as string,
    kunde: params.kunde as string,
    audit_type: params.typ as string,
    status: params.status as string,
    schwachstellen_count: params.schwachstellen as string,
    erstellt_am: params.erstellt as string,
  };
  const showBeendet = params.beendet as boolean;

  // Sort aus URL-Param parsen (Format: "field:direction")
  const sortParts = (params.sort as string).split(':');
  const sortField: SortField | null = sortParts[0] ? (sortParts[0] as SortField) : null;
  const sortDirection: 'asc' | 'desc' = sortParts[1] === 'desc' ? 'desc' : 'asc';

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [auditsData, statusesData, typesData] = await Promise.all([
          auditsService.list(),
          auditStatusService.list(),
          auditTypesService.list(),
        ]);
        setAudits(auditsData);
        setAuditStatuses(statusesData);
        setAuditTypes(typesData);
      } catch (error) {
        console.error('Fehler beim Laden der Audits:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  /* ─── Sort-Handler ─── */
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setParams({ sort: `${field}:desc` });
      } else {
        setParams({ sort: '' });
      }
    } else {
      setParams({ sort: `${field}:asc` });
    }
  };

  /* ─── Filter-Handler ─── */
  const FILTER_KEY_MAP: Record<keyof Filters, string> = {
    name: 'name',
    projektnummer: 'nummer',
    kunde: 'kunde',
    audit_type: 'typ',
    status: 'status',
    schwachstellen_count: 'schwachstellen',
    erstellt_am: 'erstellt',
  };

  const handleFilterChange = (field: keyof Filters, value: string) => {
    setParams({ [FILTER_KEY_MAP[field]]: value } as any);
  };

  /* ─── Daten-Pipeline: Filtern + Sortieren ─── */
  const displayedAudits = useMemo(() => {
    let result = [...audits];

    // 1. Beendete und abgebrochene ausblenden (wenn Toggle aus)
    if (!showBeendet) {
      result = result.filter((a) => !isClosedStatus(a.audit_status?.name));
    }

    // 2. Spaltenfilter anwenden
    if (filters.name) {
      const q = filters.name.toLowerCase();
      result = result.filter((a) => a.name.toLowerCase().includes(q));
    }
    if (filters.projektnummer) {
      const q = filters.projektnummer.toLowerCase();
      result = result.filter((a) => (a.projektnummer || '').toLowerCase().includes(q));
    }
    if (filters.kunde) {
      const q = filters.kunde.toLowerCase();
      result = result.filter((a) => (a.name_des_unternehmens || '').toLowerCase().includes(q));
    }
    if (filters.audit_type) {
      result = result.filter((a) => a.audit_types?.some((t) => t.name === filters.audit_type));
    }
    if (filters.status) {
      result = result.filter((a) => a.audit_status?.name === filters.status);
    }
    if (filters.schwachstellen_count) {
      const num = parseInt(filters.schwachstellen_count, 10);
      if (!isNaN(num)) {
        result = result.filter((a) => a.schwachstellen_count === num);
      }
    }
    if (filters.erstellt_am) {
      const q = filters.erstellt_am.toLowerCase();
      result = result.filter((a) =>
        format(new Date(a.erstellt_am), 'dd.MM.yyyy').includes(q),
      );
    }

    // 3. Sortierung
    if (sortField) {
      result.sort((a, b) => {
        let cmp = 0;
        switch (sortField) {
          case 'name':
            cmp = a.name.localeCompare(b.name, 'de');
            break;
          case 'projektnummer':
            cmp = (a.projektnummer || '').localeCompare(b.projektnummer || '', 'de');
            break;
          case 'kunde':
            cmp = (a.name_des_unternehmens || '').localeCompare(b.name_des_unternehmens || '', 'de');
            break;
          case 'audit_type':
            cmp = (a.audit_types?.[0]?.name || '').localeCompare(b.audit_types?.[0]?.name || '', 'de');
            break;
          case 'status':
            cmp = (a.audit_status?.sort_order ?? 99) - (b.audit_status?.sort_order ?? 99);
            break;
          case 'schwachstellen_count':
            cmp = a.schwachstellen_count - b.schwachstellen_count;
            break;
          case 'erstellt_am':
            cmp = new Date(a.erstellt_am).getTime() - new Date(b.erstellt_am).getTime();
            break;
        }
        return sortDirection === 'asc' ? cmp : -cmp;
      });
    } else {
      // Standardsortierung: Status sort_order, dann erstellt_am desc
      result.sort((a, b) => {
        const prioA = a.audit_status?.sort_order ?? 99;
        const prioB = b.audit_status?.sort_order ?? 99;
        if (prioA !== prioB) return prioA - prioB;
        return new Date(b.erstellt_am).getTime() - new Date(a.erstellt_am).getTime();
      });
    }

    return result;
  }, [audits, auditStatuses, filters, sortField, sortDirection, showBeendet]);

  /* ─── Navigation ─── */
  const handleCreateClick = () => {
    navigate('/audits/neu');
  };

  /* ─── Status-Farbe ─── */
  const getStatusColor = (statusName: string) => {
    switch (statusName) {
      case 'Geplant':
        return 'default';
      case 'Preflight Check':
        return 'info';
      case 'In Durchfuehrung':
        return 'warning';
      case 'Reporting':
        return 'secondary';
      case 'Ergebnisbesprechung':
        return 'primary';
      case 'Beendet':
        return 'success';
      case 'Abgebrochen':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return <TableSkeleton columns={7} rows={8} />;
  }

  /* ─── Hilfsfunktion: Sortierbare Spalte ─── */
  const renderSortLabel = (field: SortField, label: string, align?: 'center') => (
    <TableSortLabel
      active={sortField === field}
      direction={sortField === field ? sortDirection : 'asc'}
      onClick={() => handleSort(field)}
      sx={align === 'center' ? { '& .MuiTableSortLabel-icon': { ml: 0.5 } } : undefined}
    >
      {label}
    </TableSortLabel>
  );

  const filterInputSx = {
    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8125rem' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(theme.palette.divider, 0.3) },
  };

  return (
    <Box>
      <PageHeader
        title="Audits"
        breadcrumbs={[{ label: 'Audits' }]}
        onAdd={handleCreateClick}
        addLabel="Neu erstellen"
      />

      {/* Toggle Beendete + Reset-Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, ml: 0.5 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showBeendet}
              onChange={(e) => setParams({ beendet: e.target.checked })}
              size="small"
            />
          }
          label="Beendete anzeigen"
          slotProps={{ typography: { variant: 'body2', color: 'text.secondary' } }}
        />
        {hasActiveFilters && (
          <Button
            size="small"
            startIcon={<FilterListOffIcon />}
            onClick={resetParams}
            sx={{ ml: 2 }}
          >
            Filter zurücksetzen
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 860 }}>
          <TableHead>
            {/* Sortierbare Spaltenköpfe */}
            <TableRow>
              <TableCell>{renderSortLabel('name', 'Name')}</TableCell>
              <TableCell>{renderSortLabel('projektnummer', 'Projektnummer')}</TableCell>
              <TableCell>{renderSortLabel('kunde', 'Kundenname')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{renderSortLabel('audit_type', 'Audit-Typ')}</TableCell>
              <TableCell>{renderSortLabel('status', 'Status')}</TableCell>
              <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{renderSortLabel('schwachstellen_count', 'Schwachstellen', 'center')}</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{renderSortLabel('erstellt_am', 'Erstellt am')}</TableCell>
            </TableRow>

            {/* Filterzeile */}
            <TableRow>
              <TableCell sx={{ pt: 0.5, pb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Filter..."
                  value={filters.name}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                  sx={filterInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </TableCell>
              <TableCell sx={{ pt: 0.5, pb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="P-0042"
                  value={filters.projektnummer}
                  onChange={(e) => handleFilterChange('projektnummer', e.target.value)}
                  sx={filterInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </TableCell>
              <TableCell sx={{ pt: 0.5, pb: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Filter..."
                  value={filters.kunde}
                  onChange={(e) => handleFilterChange('kunde', e.target.value)}
                  sx={filterInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </TableCell>
              <TableCell sx={{ pt: 0.5, pb: 1, display: { xs: 'none', sm: 'table-cell' } }}>
                <Select
                  size="small"
                  fullWidth
                  displayEmpty
                  value={filters.audit_type}
                  onChange={(e) => handleFilterChange('audit_type', e.target.value)}
                  sx={{
                    ...filterInputSx,
                    '& .MuiSelect-select': { py: 0.75, fontSize: '0.8125rem' },
                  }}
                >
                  <MenuItem value="">
                    <em>Alle</em>
                  </MenuItem>
                  {auditTypes.map((t) => (
                    <MenuItem key={t.id} value={t.name}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {t.kuerzel && (
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              px: 0.75,
                              borderRadius: 0.75,
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              color: '#fff',
                              backgroundColor: t.farbe || '#757575',
                              height: 20,
                            }}
                          >
                            {t.kuerzel}
                          </Box>
                        )}
                        {t.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell sx={{ pt: 0.5, pb: 1 }}>
                <Select
                  size="small"
                  fullWidth
                  displayEmpty
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  sx={{
                    ...filterInputSx,
                    '& .MuiSelect-select': { py: 0.75, fontSize: '0.8125rem' },
                  }}
                >
                  <MenuItem value="">
                    <em>Alle</em>
                  </MenuItem>
                  {auditStatuses.map((s) => (
                    <MenuItem key={s.id} value={s.name}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>
              <TableCell sx={{ pt: 0.5, pb: 1, display: { xs: 'none', md: 'table-cell' } }} align="center">
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Filter..."
                  type="number"
                  value={filters.schwachstellen_count}
                  onChange={(e) => handleFilterChange('schwachstellen_count', e.target.value)}
                  sx={filterInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </TableCell>
              <TableCell sx={{ pt: 0.5, pb: 1, display: { xs: 'none', md: 'table-cell' } }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="dd.mm.yyyy"
                  value={filters.erstellt_am}
                  onChange={(e) => handleFilterChange('erstellt_am', e.target.value)}
                  sx={filterInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {displayedAudits.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Keine Audits gefunden
                </TableCell>
              </TableRow>
            ) : (
              displayedAudits.map((audit) => (
                <TableRow
                  key={audit.id}
                  hover
                  {...navigableProps(navigate, `/audits/${audit.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {audit.name}
                      <Chip
                        label={audit.sprache?.toUpperCase() || 'DE'}
                        size="small"
                        variant="outlined"
                        sx={{
                          height: 18,
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                      {isAuditShared(audit.id) && (
                        <Tooltip title="In der isolierten Umgebung (Restricted-Login) zum Lesen freigegeben">
                          <VisibilityIcon sx={{ fontSize: 16, color: 'warning.main' }} data-testid={`audit-shared-${audit.id}`} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{audit.projektnummer || '–'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-line' }}>{audit.name_des_unternehmens || ''}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    {audit.audit_types?.length > 0 ? (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {audit.audit_types.map((t) => (
                          t.kuerzel ? (
                            <Box
                              key={t.id}
                              component="span"
                              title={t.name}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1,
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                color: '#fff',
                                backgroundColor: t.farbe || '#757575',
                                height: 24,
                                letterSpacing: '0.03em',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {t.kuerzel}
                            </Box>
                          ) : (
                            <span key={t.id}>{t.name}</span>
                          )
                        ))}
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={audit.audit_status?.name || 'Kein Status'}
                      size="small"
                      color={getStatusColor(audit.audit_status?.name || '') as any}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Chip
                      label={audit.schwachstellen_count}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        minWidth: 32,
                        bgcolor: audit.schwachstellen_count > 0 ? (isDark ? '#1e3a5f' : alpha('#42a5f5', 0.1)) : undefined,
                        color: audit.schwachstellen_count > 0 ? (isDark ? '#90caf9' : '#1565c0') : undefined,
                      }}
                      variant={audit.schwachstellen_count > 0 ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    {format(new Date(audit.erstellt_am), 'dd.MM.yyyy')}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AuditsListPage;
